/**
 * SectionCanvas — displays the ordered list of template sections
 * with controls to reorder, remove, and select for editing.
 */
import React from 'react';
import {
  Box, Divider, IconButton, Paper, Tooltip, Typography
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TuneIcon from '@mui/icons-material/Tune';

export default function SectionCanvas({ sections = [], selectedIndex, onSelect, onMove, onRemove }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {sections.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          Add a section from the palette on the left.
        </Typography>
      )}
      {sections.map((section, idx) => (
        <Paper
          key={idx}
          variant="outlined"
          sx={{
            p: 1.5,
            cursor: 'pointer',
            border: selectedIndex === idx ? '2px solid' : '1px solid',
            borderColor: selectedIndex === idx ? 'primary.main' : 'divider',
          }}
          onClick={() => onSelect(idx)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Move buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Tooltip title="Move up">
                <span>
                  <IconButton size="small" disabled={idx === 0} onClick={(e) => { e.stopPropagation(); onMove(idx, idx - 1); }}>
                    <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton size="small" disabled={idx === sections.length - 1} onClick={(e) => { e.stopPropagation(); onMove(idx, idx + 1); }}>
                    <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>
                {section.title || section.type || `Section ${idx + 1}`}
              </Typography>
              {section.type && (
                <Typography variant="caption" color="text.secondary">
                  {section.type}
                </Typography>
              )}
            </Box>

            <Tooltip title="Configure">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onSelect(idx); }}>
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove section">
              <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onRemove(idx); }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Preview snippet */}
          {section.content && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
              }}>
                {typeof section.content === 'string' ? section.content : JSON.stringify(section.content)}
              </Typography>
            </>
          )}
        </Paper>
      ))}
    </Box>
  );
}
